import { useCallback, useMemo, useState } from 'react';
import { Chip, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { GridColDef } from '@mui/x-data-grid';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { QuickFilter, RowAction } from '../../components/data-grid/RouteDataGrid';
import { fmtDateTime } from '../../utils/format';
import type { ExportColumn } from '../../utils/export';
import type { IotQrBatchSummary } from '../../api/types';
import {
  useMyPermissions,
  useIotQrBatchesList,
} from '../../api/queries';
import { apiBaseUrl, TOKEN_STORAGE_KEY } from '../../auth/config';
import { GenerateIotQrBatchDialog } from './GenerateIotQrBatchDialog';
import { useAlert } from '../../components/dialogs/DialogProvider';

/**
 * ESP32 Deployment Plan §3 M1 — IOT (In Mac) QR batches.
 *
 * ARMS-only factory surface. Each batch mints N IoT QR pairs to be
 * factory-applied to ESP32-P4 HAT stickers. Distinct from SC QR batches
 * (which sticker site controllers) and machine QR batches (which sticker
 * EGM cabinets — those land at M6).
 */
export default function IotQrBatchesPage() {
  usePageTitle('IoT (HAT) QR batches');
  const perms = useMyPermissions();
  const has = useCallback(
    (code: string) => perms.data?.codes?.includes(code) ?? false,
    [perms.data?.codes],
  );

  const navigate = useNavigate();
  const alert = useAlert();
  const { queryKey, listFn } = useIotQrBatchesList();
  const [generateOpen, setGenerateOpen] = useState(false);

  const columns = useMemo<GridColDef<IotQrBatchSummary>[]>(() => [
    { field: 'code', headerName: 'Batch', width: 200,
      renderCell: (p) => <code>{p.row.code}</code> },
    { field: 'generatedCount', headerName: 'Generated', width: 110,
      renderCell: (p) => p.row.generatedCount.toLocaleString() },
    { field: 'boundCount', headerName: 'Bound', width: 110,
      renderCell: (p) => `${p.row.boundCount.toLocaleString()} / ${p.row.generatedCount.toLocaleString()}` },
    { field: 'status', headerName: 'Status', width: 130,
      renderCell: (p) => <BatchStatusChip status={p.row.status} /> },
    { field: 'notes', headerName: 'Notes', flex: 1,
      renderCell: (p) => p.row.notes
        ? <Typography variant="body2">{p.row.notes}</Typography>
        : <Typography variant="body2" color="text.secondary">—</Typography> },
    { field: 'createdAt', headerName: 'Created', width: 180,
      valueFormatter: (value) => fmtDateTime(value) },
  ], []);

  const quickFilters = useMemo<QuickFilter<IotQrBatchSummary>[]>(() => [
    { label: 'All',     filter: () => true },
    { label: 'Printed', filter: (b) => b.status === 'printed' },
    { label: 'Voided',  filter: (b) => b.status === 'voided' },
  ], []);

  const exportColumns = useMemo<ExportColumn<IotQrBatchSummary>[]>(() => [
    { key: 'code',           label: 'Batch code' },
    { key: 'requestedCount', label: 'Requested' },
    { key: 'generatedCount', label: 'Generated' },
    { key: 'boundCount',     label: 'Bound' },
    { key: 'status',         label: 'Status' },
    { key: 'notes',          label: 'Notes' },
    { key: 'createdAt',      label: 'Created', format: (v) => fmtDateTime(v) },
  ], []);

  const actions = useMemo<RowAction<IotQrBatchSummary>[]>(() => [
    { label: 'Open',         onClick: (row) => navigate(`/factory/iot-qr-batches/${row.id}`) },
    { label: 'Print',        onClick: (row) => navigate(`/factory/iot-qr-batches/${row.id}/print`) },
    { label: 'Download CSV', onClick: (row) => downloadCsv(row.id, row.code, alert) },
  ], [navigate, alert]);

  if (!has('provisioning.factory.read')) {
    return (
      <Typography color="text.secondary">
        You don't have permission to view IoT QR batches.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h4">IoT (HAT) QR batches</Typography>
      <Typography variant="body2" color="text.secondary">
        ARMS-only factory surface. Each batch mints N IoT QR pairs — single
        labels factory-applied to ESP32-P4 HAT stickers. The Mac flashing
        tool scans these QRs at the bench to bind a freshly-flashed HAT to
        its identity row.
      </Typography>

      <RouteDataGrid<IotQrBatchSummary>
        entityName="IoT QR batches"
        queryKey={queryKey}
        listFn={listFn}
        columns={columns}
        searchFields={[{ kind: 'text', key: 'code', label: 'Batch code' }]}
        quickFilters={quickFilters}
        exportColumns={exportColumns}
        reportTitle="ARMS — IoT (HAT) QR batches"
        actions={actions}
        primaryAction={
          has('provisioning.factory.mint')
            ? { label: '+ Generate batch', onClick: () => setGenerateOpen(true) }
            : undefined
        }
        onRowClick={(row) => navigate(`/factory/iot-qr-batches/${row.id}`)}
        emptyMessage="No IoT QR batches yet — generate one to get started."
      />

      <GenerateIotQrBatchDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
      />
    </Stack>
  );
}

function BatchStatusChip({ status }: { status: string }) {
  const color =
    status === 'printed' ? 'success' :
    status === 'created' ? 'info' :
    status === 'voided'  ? 'error' :
    'default';
  return <Chip size="small" label={status} color={color as 'success' | 'info' | 'error' | 'default'} />;
}

async function downloadCsv(
  batchId: string, batchCode: string,
  alert: (opts: { title: string; message: string; severity?: 'info' | 'warning' | 'error' | 'success' }) => Promise<void>,
) {
  const url = `${apiBaseUrl}/api/v1/factory/iot-qr-batches/${batchId}/export.csv`;
  const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
  if (!token) {
    await alert({ title: 'Not signed in', message: 'Sign in and retry the download.', severity: 'warning' });
    return;
  }
  try {
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) {
      const text = await r.text();
      throw new Error(`HTTP ${r.status}: ${text || r.statusText}`);
    }
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `iot-qr-batch-${batchCode}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  } catch (e) {
    await alert({ title: 'Download failed', message: (e as Error).message, severity: 'error' });
  }
}
